// src/components/PortfolioGrowthChart.js - VERSION COMPLETE DÉBOGUÉE
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';

const PortfolioGrowthChart = ({ data = [] }) => {
    const { t, ready } = useTranslation();
    
    // 🚨 DEBUG: Vérifier l'état des traductions et des données
    console.log('🌍 i18n ready:', ready);
    console.log('📊 PortfolioGrowthChart - Données reçues:', data);
    console.log('📊 Type des données:', typeof data);
    console.log('📊 Longueur des données:', data?.length);

    // Fonction pour formater la date
    const formatDateToShort = (dateString) => {
        try {
            if (!dateString) return '';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.warn('⚠️ Date invalide:', dateString);
                return dateString;
            }
            const formatted = `${date.getDate()} ${date.toLocaleDateString('fr-FR', { month: 'short' })}`;
            console.log('📅 Date formatée:', dateString, '->', formatted);
            return formatted;
        } catch (error) {
            console.error('❌ Erreur formatDate:', error);
            return dateString;
        }
    };

    // Formatage des montants avec fallback
    const formatCurrency = (value) => {
        try {
            if (typeof value !== 'number' || isNaN(value)) {
                console.warn('⚠️ Valeur invalide pour formatCurrency:', value);
                return '0 FCFA';
            }
            
            try {
                const formatted = new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'XOF',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(value);
                console.log('💰 Valeur formatée:', value, '->', formatted);
                return formatted;
            } catch (e) {
                console.warn('⚠️ Fallback formatCurrency manuel pour:', value);
                return value.toLocaleString('fr-FR') + ' FCFA';
            }
        } catch (error) {
            console.error('❌ Erreur formatCurrency:', error);
            return '0 FCFA';
        }
    };

    // ✅ Validation améliorée des données
    const chartData = React.useMemo(() => {
        console.log('🔄 useMemo - Traitement des données');
        
        if (!Array.isArray(data)) {
            console.error('❌ Les données ne sont pas un tableau:', data);
            return [];
        }
        
        if (data.length === 0) {
            console.warn('⚠️ Tableau de données vide');
            return [];
        }

        const processedData = data
            .filter((item, index) => {
                const isValid = item && (item.date || item.name);
                if (!isValid) console.warn(`⚠️ Élément invalide à l'index ${index}:`, item);
                return isValid;
            })
            .map((item, index) => {
                const value = Number(item.value || item.balance || 0);
                const processedItem = {
                    name: formatDateToShort(item.date || item.name),
                    balance: isNaN(value) ? 0 : value,
                    originalDate: item.date || item.name,
                    originalValue: isNaN(value) ? 0 : value
                };
                console.log(`🔍 Item ${index} traité:`, processedItem);
                return processedItem;
            });
        
        console.log('✅ Données finales pour le graphique:', processedData);
        return processedData;
    }, [data]);

    // Tooltip personnalisé
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;
            if (data) {
                console.log('🎯 Tooltip activé:', data);
                return (
                    <div className="custom-tooltip bg-white p-3 border rounded shadow">
                        <p className="font-weight-bold mb-1">
                            {ready && t ? t('date') : 'Date'}: {data.originalDate || label}
                        </p>
                        <p className="text-primary mb-0">
                            {ready && t ? t('portfolio_value') : 'Valeur'}: {formatCurrency(data.originalValue)}
                        </p>
                    </div>
                );
            }
        }
        return null;
    };

    // 🚨 Message d'erreur détaillé avec fallback
    if (!chartData || chartData.length === 0) {
        console.warn('⚠️ Aucune donnée à afficher dans le graphique');
        return (
            <div style={{ width: '100%', height: 300 }} className="d-flex align-items-center justify-content-center">
                <div className="text-center text-muted">
                    <p className="mb-2">
                        {ready && t ? t('no_chart_data_available') : 'Aucune donnée disponible'}
                    </p>
                    <small>
                        {ready && t ? t('chart_will_appear_with_real_data') : 'Le graphique apparaîtra avec des données réelles'}
                    </small>
                    <br />
                    <small className="text-danger">
                        Format attendu: [{`{date: '2024-01-01', value: 1000}`}, ...]
                    </small>
                    <br />
                    <small className="text-info">
                        Données reçues: {JSON.stringify(data)}
                    </small>
                </div>
            </div>
        );
    }

    console.log('🎨 Rendu du graphique avec:', chartData.length, 'points');

    return (
        <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{
                        top: 5,
                        right: 10,
                        left: 0,
                        bottom: 5,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                        dataKey="name" 
                        stroke="#555"
                        tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                        stroke="#555" 
                        tickFormatter={formatCurrency}
                        tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Line 
                        type="monotone" 
                        dataKey="balance" 
                        stroke="#8884d8" 
                        activeDot={{ r: 6 }} 
                        name={ready && t ? t('portfolio_growth') : 'Croissance'}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default PortfolioGrowthChart;